import { z } from "zod"

import { periodOptions, reportingIntervalOptions } from "./staffing-shared"

/**
 * Shared validation constants and schemas for use by both frontend and edge function.
 * These ensure consistent validation rules across the application.
 */

// ============================================================================
// VALIDATION LIMITS
// ============================================================================

export const INPUT_LIMITS = {
  incomingContacts: { min: 1, max: 1_000_000 },
  averageHandleTimeSeconds: { min: 1, max: 7200 }, // 2 hours max
  requiredServiceLevel: { min: 1, max: 100 }, // 0% SLA doesn't make business sense
  targetAnswerTimeSeconds: { min: 0, max: 3600 }, // 0 = immediate answer, 1 hour max
  shrinkage: { min: 0, max: 99 }, // 100% = nobody available
  maxOccupancy: { min: 1, max: 100 }, // 0% doesn't make sense
  averagePatienceSeconds: { min: 1, max: 7200 }, // 2 hours max
  workingHoursPerWeek: { min: 1, max: 168 }, // 168 hours = 7 days * 24 hours
} as const

// ============================================================================
// PERIOD AND REPORTING INTERVAL VALIDATION
// ============================================================================

const periodValues = new Set(
  periodOptions.filter((option) => !option.disabled).map((option) => option.value),
)

const reportingIntervalValues = new Set(reportingIntervalOptions.map((option) => option.value))

// ============================================================================
// HELPER FUNCTIONS FOR VALIDATION
// ============================================================================

const addIssue = (ctx: z.RefinementCtx, message: string) =>
  ctx.addIssue({ code: z.ZodIssueCode.custom, message })

// ============================================================================
// NUMBER VALIDATION SCHEMAS (for edge function - expects numbers)
// ============================================================================

/**
 * Schema for validating numeric input fields in the edge function.
 * These schemas expect actual numbers, not strings.
 */
export const numericInputSchema = z.object({
  incomingContacts: z
    .number()
    .int("Incoming contacts must be a whole number")
    .min(
      INPUT_LIMITS.incomingContacts.min,
      `Incoming contacts must be at least ${INPUT_LIMITS.incomingContacts.min}`,
    )
    .max(
      INPUT_LIMITS.incomingContacts.max,
      `Incoming contacts cannot exceed ${INPUT_LIMITS.incomingContacts.max.toLocaleString()}`,
    ),

  period: z.string().refine((value) => periodValues.has(value), {
    message: "Please select a valid time period",
  }),

  averageHandleTimeSeconds: z
    .number()
    .min(
      INPUT_LIMITS.averageHandleTimeSeconds.min,
      `Average handling time must be at least ${INPUT_LIMITS.averageHandleTimeSeconds.min} second`,
    )
    .max(
      INPUT_LIMITS.averageHandleTimeSeconds.max,
      `Average handling time cannot exceed ${INPUT_LIMITS.averageHandleTimeSeconds.max} seconds (2 hours)`,
    ),

  requiredServiceLevel: z
    .number()
    .min(
      INPUT_LIMITS.requiredServiceLevel.min,
      `Service level must be at least ${INPUT_LIMITS.requiredServiceLevel.min}%`,
    )
    .max(
      INPUT_LIMITS.requiredServiceLevel.max,
      `Service level cannot exceed ${INPUT_LIMITS.requiredServiceLevel.max}%`,
    ),

  targetAnswerTimeSeconds: z
    .number()
    .min(
      INPUT_LIMITS.targetAnswerTimeSeconds.min,
      `Target answer time must be at least ${INPUT_LIMITS.targetAnswerTimeSeconds.min} seconds`,
    )
    .max(
      INPUT_LIMITS.targetAnswerTimeSeconds.max,
      `Target answer time cannot exceed ${INPUT_LIMITS.targetAnswerTimeSeconds.max} seconds (1 hour)`,
    ),

  shrinkage: z
    .number()
    .min(INPUT_LIMITS.shrinkage.min, `Shrinkage must be at least ${INPUT_LIMITS.shrinkage.min}%`)
    .max(INPUT_LIMITS.shrinkage.max, `Shrinkage cannot exceed ${INPUT_LIMITS.shrinkage.max}%`),

  maxOccupancy: z
    .number()
    .min(
      INPUT_LIMITS.maxOccupancy.min,
      `Max occupancy must be at least ${INPUT_LIMITS.maxOccupancy.min}%`,
    )
    .max(
      INPUT_LIMITS.maxOccupancy.max,
      `Max occupancy cannot exceed ${INPUT_LIMITS.maxOccupancy.max}%`,
    ),

  averagePatienceSeconds: z
    .number()
    .min(
      INPUT_LIMITS.averagePatienceSeconds.min,
      `Average patience must be at least ${INPUT_LIMITS.averagePatienceSeconds.min} second`,
    )
    .max(
      INPUT_LIMITS.averagePatienceSeconds.max,
      `Average patience cannot exceed ${INPUT_LIMITS.averagePatienceSeconds.max} seconds (2 hours)`,
    ),

  // Optional fields
  workingHoursPerWeek: z
    .number()
    .min(
      INPUT_LIMITS.workingHoursPerWeek.min,
      `Working hours per week must be at least ${INPUT_LIMITS.workingHoursPerWeek.min}`,
    )
    .max(
      INPUT_LIMITS.workingHoursPerWeek.max,
      `Working hours per week cannot exceed ${INPUT_LIMITS.workingHoursPerWeek.max}`,
    )
    .optional(),

  reportingInterval: z
    .string()
    .refine((value) => reportingIntervalValues.has(value), {
      message: "Please select a valid reporting interval",
    })
    .optional(),
})

// ============================================================================
// STRING VALIDATION SCHEMAS (for frontend - form values are strings)
// ============================================================================

/**
 * Helper to create a required string validator
 */
const requiredString = (message: string) => z.string().trim().min(1, { message })

/**
 * Validates positive integer strings with i18n error messages
 */
const positiveIntegerString = ({
  requiredMessage,
  positiveMessage,
  integerMessage,
  rangeMessage,
}: {
  requiredMessage: string
  positiveMessage: string
  integerMessage: string
  rangeMessage: string
}) =>
  requiredString(requiredMessage).superRefine((value, ctx) => {
    const numeric = Number(value)

    if (!Number.isFinite(numeric) || numeric <= 0) {
      addIssue(ctx, positiveMessage)
      return
    }

    if (!Number.isInteger(numeric)) {
      addIssue(ctx, integerMessage)
      return
    }

    // Range validation is now handled by rangeMessage i18n key
    if (
      numeric < INPUT_LIMITS.incomingContacts.min ||
      numeric > INPUT_LIMITS.incomingContacts.max
    ) {
      addIssue(ctx, rangeMessage)
    }
  })

/**
 * Validates positive number strings with i18n error messages
 */
const positiveNumberString = ({
  requiredMessage,
  positiveMessage,
  rangeMessage,
  min,
  max,
}: {
  requiredMessage: string
  positiveMessage: string
  rangeMessage: string
  min: number
  max: number
}) =>
  requiredString(requiredMessage).superRefine((value, ctx) => {
    const numeric = Number(value)

    if (!Number.isFinite(numeric) || numeric <= 0) {
      addIssue(ctx, positiveMessage)
      return
    }

    // Range validation uses i18n key
    if (numeric < min || numeric > max) {
      addIssue(ctx, rangeMessage)
    }
  })

/**
 * Validates percentage range strings with custom error messages
 */
const percentRangeString = ({
  requiredMessage,
  rangeMessage,
  min,
  max,
  inclusiveMin,
  inclusiveMax,
}: {
  requiredMessage: string
  rangeMessage: string
  min: number
  max: number
  inclusiveMin: boolean
  inclusiveMax: boolean
}) =>
  requiredString(requiredMessage).superRefine((value, ctx) => {
    const numeric = Number(value)
    const lowerBound = inclusiveMin ? numeric >= min : numeric > min
    const upperBound = inclusiveMax ? numeric <= max : numeric < max

    if (!Number.isFinite(numeric) || !lowerBound || !upperBound) {
      addIssue(ctx, rangeMessage)
    }
  })

/**
 * Schema for validating string input fields from the frontend form.
 * These schemas expect strings since HTML form values are always strings.
 */
export const stringInputSchema = z.object({
  incomingContacts: positiveIntegerString({
    requiredMessage: "form.errors.incomingContacts.required",
    positiveMessage: "form.errors.incomingContacts.positive",
    integerMessage: "form.errors.incomingContacts.integer",
    rangeMessage: "form.errors.incomingContacts.range",
  }),

  period: z
    .string()
    .trim()
    .refine((value) => periodValues.has(value), { message: "form.errors.period.invalid" }),

  averageHandleTimeSeconds: positiveNumberString({
    requiredMessage: "form.errors.averageHandleTimeSeconds.required",
    positiveMessage: "form.errors.averageHandleTimeSeconds.positive",
    rangeMessage: "form.errors.averageHandleTimeSeconds.range",
    min: INPUT_LIMITS.averageHandleTimeSeconds.min,
    max: INPUT_LIMITS.averageHandleTimeSeconds.max,
  }),

  requiredServiceLevel: percentRangeString({
    requiredMessage: "form.errors.requiredServiceLevel.required",
    rangeMessage: "form.errors.requiredServiceLevel.range",
    min: INPUT_LIMITS.requiredServiceLevel.min,
    max: INPUT_LIMITS.requiredServiceLevel.max,
    inclusiveMin: true,
    inclusiveMax: true,
  }),

  targetAnswerTimeSeconds: positiveNumberString({
    requiredMessage: "form.errors.targetAnswerTimeSeconds.required",
    positiveMessage: "form.errors.targetAnswerTimeSeconds.positive",
    rangeMessage: "form.errors.targetAnswerTimeSeconds.range",
    min: INPUT_LIMITS.targetAnswerTimeSeconds.min,
    max: INPUT_LIMITS.targetAnswerTimeSeconds.max,
  }),

  shrinkage: percentRangeString({
    requiredMessage: "form.errors.shrinkage.required",
    rangeMessage: "form.errors.shrinkage.range",
    min: INPUT_LIMITS.shrinkage.min,
    max: INPUT_LIMITS.shrinkage.max,
    inclusiveMin: true,
    inclusiveMax: false, // max is 99, not 100
  }),

  maxOccupancy: percentRangeString({
    requiredMessage: "form.errors.maxOccupancy.required",
    rangeMessage: "form.errors.maxOccupancy.range",
    min: INPUT_LIMITS.maxOccupancy.min,
    max: INPUT_LIMITS.maxOccupancy.max,
    inclusiveMin: true,
    inclusiveMax: true,
  }),

  averagePatienceSeconds: positiveNumberString({
    requiredMessage: "form.errors.averagePatienceSeconds.required",
    positiveMessage: "form.errors.averagePatienceSeconds.positive",
    rangeMessage: "form.errors.averagePatienceSeconds.range",
    min: INPUT_LIMITS.averagePatienceSeconds.min,
    max: INPUT_LIMITS.averagePatienceSeconds.max,
  }),

  workingHoursPerWeek: positiveNumberString({
    requiredMessage: "form.errors.workingHoursPerWeek.required",
    positiveMessage: "form.errors.workingHoursPerWeek.positive",
    rangeMessage: "form.errors.workingHoursPerWeek.range",
    min: INPUT_LIMITS.workingHoursPerWeek.min,
    max: INPUT_LIMITS.workingHoursPerWeek.max,
  }),

  reportingInterval: z
    .string()
    .trim()
    .refine((value) => reportingIntervalValues.has(value), {
      message: "form.errors.reportingInterval.invalid",
    }),
})

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type NumericInputData = z.infer<typeof numericInputSchema>
export type StringInputData = z.infer<typeof stringInputSchema>
